import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, format, isSameMonth, isToday,
} from "date-fns";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useOverview, useDeleteShift } from "../lib/queries";
import { monthKey, fmtEur, fmtHours } from "../lib/api";
import { ShiftRow } from "../components/ShiftRow";
import { StatCards } from "../components/StatCards";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const MonthGrid = ({ month, byDay, selected, onSelect }) => {
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }),
  });
  return (
    <div className="rounded-2xl border border-border/70 bg-card p-3 sm:p-5">
      <div className="grid grid-cols-7 gap-1 pb-2">
        {WEEKDAYS.map((d) => <div key={d} className="text-center text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const key = format(d, "yyyy-MM-dd");
          const shifts = byDay[key] || [];
          const inMonth = isSameMonth(d, month);
          const active = key === selected;
          const ot = shifts.some((s) => s.overtime_minutes > 0);
          return (
            <button key={key} data-testid={`calendar-day-${key}`} disabled={!inMonth} onClick={() => onSelect(key)}
              className={`relative flex aspect-square flex-col items-center justify-center rounded-xl text-sm transition-colors duration-150 sm:aspect-[1.15]
                ${!inMonth ? "opacity-20" : "hover:bg-secondary"}
                ${active ? "bg-amber-500 font-bold text-[#0B0F17] hover:bg-amber-400" : shifts.length ? "bg-secondary/70" : ""}
                ${isToday(d) && !active ? "ring-1 ring-amber-500/70" : ""}`}>
              <span className="font-num">{format(d, "d")}</span>
              {shifts.length > 0 && (
                <span className="mt-1 flex gap-0.5">
                  {shifts.slice(0, 3).map((s) => (
                    <span key={s.id} className={`h-1.5 w-1.5 rounded-full ${active ? "bg-[#0B0F17]" : s.overnight ? "bg-indigo-400" : "bg-amber-500"}`} />
                  ))}
                  {ot && !active && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap gap-4 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" />Day shift</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-indigo-400" />Night shift</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-400" />Overtime</span>
      </div>
    </div>
  );
};

const DayPanel = ({ day, shifts, onDelete }) => (
  <div data-testid="calendar-day-panel" className="space-y-3">
    <div className="flex items-center justify-between">
      <h3 className="font-heading text-2xl font-extrabold uppercase">{format(new Date(day + "T00:00"), "EEEE d MMM")}</h3>
      <Link to={`/shifts/new?date=${day}`} data-testid="calendar-add-shift-day"
        className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1.5 text-xs font-bold text-[#0B0F17] hover:bg-amber-400">
        <Plus className="h-3.5 w-3.5" /> Add
      </Link>
    </div>
    {shifts.length === 0 ? (
      <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No shifts on this day.</p>
    ) : (
      <>
        {shifts.map((s) => <ShiftRow key={s.id} shift={s} onDelete={onDelete} showDate={false} />)}
        <p className="text-right text-xs text-muted-foreground">
          Day total: <span className="font-num font-bold text-foreground">{fmtHours(shifts.reduce((a, s) => a + s.paid_minutes, 0))}</span> ·{" "}
          <span className="font-num font-bold text-amber-400">{fmtEur(shifts.reduce((a, s) => a + s.earnings, 0))}</span>
        </p>
      </>
    )}
  </div>
);

export default function CalendarPage() {
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [selected, setSelected] = useState(format(new Date(), "yyyy-MM-dd"));
  const { data } = useOverview(monthKey(month));
  const del = useDeleteShift();
  const byDay = useMemo(() => {
    const m = {};
    (data?.shifts || []).forEach((s) => { (m[s.date] = m[s.date] || []).push(s); });
    return m;
  }, [data]);

  const go = (m) => { setMonth(m); setSelected(isSameMonth(m, new Date()) ? format(new Date(), "yyyy-MM-dd") : format(m, "yyyy-MM-dd")); };

  return (
    <div data-testid="calendar-page" className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <h1 data-testid="calendar-month-label" className="font-heading text-4xl font-black uppercase tracking-tight sm:text-5xl">
          {format(month, "MMMM")} <span className="text-muted-foreground">{format(month, "yyyy")}</span>
        </h1>
        <div className="flex gap-2">
          <button data-testid="calendar-prev-month" onClick={() => go(subMonths(month, 1))} aria-label="Previous month"
            className="rounded-full border border-border p-2.5 hover:bg-secondary"><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="calendar-next-month" onClick={() => go(addMonths(month, 1))} aria-label="Next month"
            className="rounded-full border border-border p-2.5 hover:bg-secondary"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-7"><MonthGrid month={month} byDay={byDay} selected={selected} onSelect={setSelected} /></div>
        <div className="lg:col-span-5"><DayPanel day={selected} shifts={byDay[selected] || []} onDelete={(id) => del.mutate(id)} /></div>
      </div>
      {data && (
        <section data-testid="monthly-overview" className="space-y-4">
          <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight">Monthly overview</h2>
          <StatCards totals={data.totals} settings={data.settings} />
          <div className="grid grid-cols-2 gap-3 rounded-2xl border border-border/70 bg-card p-5 text-sm sm:grid-cols-4">
            <div><div className="text-xs text-muted-foreground">Regular hours</div><div data-testid="overview-regular-hours" className="font-num font-bold">{data.totals.regular_hours}h</div></div>
            <div><div className="text-xs text-muted-foreground">Overtime hours</div><div className="font-num font-bold text-emerald-400">{data.totals.overtime_hours}h</div></div>
            <div><div className="text-xs text-muted-foreground">Breaks taken</div><div className="font-num font-bold">{data.totals.break_hours}h</div></div>
            <div><div className="text-xs text-muted-foreground">Avg per shift</div><div className="font-num font-bold">{data.totals.shift_count ? (data.totals.paid_hours / data.totals.shift_count).toFixed(1) : 0}h</div></div>
          </div>
        </section>
      )}
    </div>
  );
}
